import { Box, HBox, VBox } from "@ui/box/Box";
import { GD } from "src/GD";
import { Skeleton } from "@ui/skeleton/Skeleton";
import { Typo } from "@ui/text/Typo";
import { PositiveSizeChart } from "@ui/chart/PositiveSizeChart";
import Spacer from "@ui/spacer/Spacer";
import { IconArrowRight } from "@ui/icons/IconArrowRight";
import { type ReactNode, useMemo, useEffect, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { USER_ROLE } from "src/services/AuthService";
import { useRouter } from "src/services/Router";
import { S_UNIVERSAL_DIALOG } from "../components/universalDialog/signals";
import { FormParser } from "src/view/FormParser";
import { StatusBadge } from "../components/StatusBadge";
import { TeamDetails } from "../components/TeamDetails";
import { UserDetails } from "../components/UserDetails";
import { Page } from "@ui/page/Page";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { DEFAULT_CURRENCY, MAX_USER_DETAILS_WIDTH } from "src/constants";
import { toast } from "react-toastify";
import {
  amountValidationError,
  parsePositiveAmountToMinorUnits,
} from "src/utils/amounts";
import { buildBuyerCardFinancials } from "src/utils/buyerFinancials";
import {
  buildDashboardDailyActivity,
  buildDashboardDateKeys,
  buildDashboardStatusTrend,
  countDashboardTransactionsByBuyer,
  getDashboardPeriodMetricLabel,
  isNoLimitTransactionResponseIncomplete,
  sumDashboardCardBalances,
  summarizeDashboardTransactionStatuses,
  toDashboardDateKey,
} from "src/utils/dashboardSpend";
import { useIssuer } from "../issuer/IssuerProvider";
import { getDashboardBuyerBalance } from "src/utils/buyerBalances";
import {
  buildDashboardDeclinedBreakdown,
  buildDashboardDeclinedChart,
} from "src/utils/declinedAnalytics";
import { TransactionDetails } from "../components/TransactionDetails";
import { CardSpendStatusRatio } from "../components/CardSpendStatusRatio";
import { CardSpendStatusTooltip } from "../components/CardSpendStatusTooltip";
import {
  getTransactionAmountPresentation,
  getTransactionDisplayLabel,
} from "src/utils/transactionPresentation";
import { DS, HeroButton, HeroStat, StatGroup, Avatar } from "../ds/Kit";

const SPEND_OVERVIEW_MONTH_DAYS = 30;
const SPEND_OVERVIEW_WEEK_DAYS = 7;
const SPEND_OVERVIEW_DAY_DAYS = 1;
const ANALYTICS_PERIOD_FORTNIGHT_DAYS = 14;
const ANALYTICS_PERIOD_OPTIONS = [
  SPEND_OVERVIEW_WEEK_DAYS,
  ANALYTICS_PERIOD_FORTNIGHT_DAYS,
  SPEND_OVERVIEW_MONTH_DAYS,
] as const;
type AnalyticsPeriodDays = (typeof ANALYTICS_PERIOD_OPTIONS)[number];
type CardsStatusChartEntry = { name: string; value: number; color: string };
type DeclinedMode = "teams" | "buyers";

const DASHBOARD_BRAND = "#2B5C8A";
const DASHBOARD_TOOLTIP = "#111827";
const DASHBOARD_SERIES_COLORS = [
  DASHBOARD_BRAND,
  "#dc2626",
  "#10b981",
  "#f59e0b",
  "#3f6e9a",
  "#06b6d4",
];

const PIE_COLORS = [
  DASHBOARD_BRAND,
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#3f6e9a",
  "#06b6d4",
];
const AXIS_TICK = { fill: DS.muted, fontSize: 11, fontWeight: 500 } as const;
const TOOLTIP_PROPS = {
  contentStyle: {
    borderRadius: "10px",
    backgroundColor: DASHBOARD_TOOLTIP,
    border: "none",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    padding: "8px 12px",
  },
  itemStyle: { color: "#38bdf8", fontWeight: 600, fontSize: "12px" },
  labelStyle: { color: "#94a3b8", fontSize: "11px", marginBottom: "4px", fontWeight: 500 },
} as const;
const formatActivityDate = (value: string) =>
  new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Pill group with aria-pressed buttons, used for every period/mode switch on the page. */
const PeriodToggle = <T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly { key: T; label: string; ariaLabel?: string }[];
  value: T;
  onChange: (key: T) => void;
  label: string;
}) => (
  <HBox
    role="group"
    aria-label={label}
    sx={{
      gap: "2px",
      padding: "3px",
      borderRadius: "10px",
      backgroundColor: DS.soft,
      border: `1px solid ${DS.line}`,
      // Segments share the width and may shrink, so a narrow stat column never clips "Today".
      width: "100%",
      maxWidth: "320px",
      minWidth: 0,
      boxSizing: "border-box",
    }}
  >
    {options.map((option) => {
      const active = option.key === value;
      return (
        <Box
          key={String(option.key)}
          element="button"
          type="button"
          aria-pressed={active}
          aria-label={option.ariaLabel}
          onClick={() => onChange(option.key)}
          sx={{
            border: 0,
            fontFamily: "inherit",
            cursor: "pointer",
            minHeight: "30px",
            flex: "1 1 0",
            minWidth: 0,
            padding: "0 2px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            color: active ? DS.accentInk : DS.muted,
            backgroundColor: active ? DS.surface : "transparent",
            boxShadow: active ? "0 1px 3px rgba(15, 23, 42, 0.1)" : "none",
            transition: "all .15s ease",
            "&:hover": { color: DS.accentInk },
          }}
        >
          {option.label}
        </Box>
      );
    })}
  </HBox>
);

const TextLink = ({ children, onClick }: { children: string; onClick: () => void }) => (
  <Box
    element="button"
    type="button"
    onClick={onClick}
    sx={{
      border: 0,
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      fontSize: "13px",
      fontWeight: 600,
      color: DS.accent,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      "&:hover": { color: DS.accentInk, textDecoration: "underline" },
    }}
  >
    {children}
    <IconArrowRight width="12px" height="12px" />
  </Box>
);

const ChartCard = ({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <VBox
    gap="14px"
    sx={{
      padding: "18px 20px 20px",
      borderRadius: DS.radius,
      border: `1px solid ${DS.line}`,
      backgroundColor: DS.surface,
      boxShadow: DS.shadow,
      minWidth: 0,
    }}
  >
    <HBox
      sx={{
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "12px",
        flexWrap: "wrap",
      }}
    >
      <VBox gap="2px" sx={{ minWidth: 0 }}>
        <Typo sx={{ fontSize: "16px", fontWeight: 650, color: DS.ink }}>{title}</Typo>
        {subtitle && (
          <Typo sx={{ fontSize: "13px", color: DS.muted }}>{subtitle}</Typo>
        )}
      </VBox>
      {action}
    </HBox>
    {children}
  </VBox>
);

const EmptyNote = ({ title, hint }: { title: string; hint?: string }) => (
  <VBox
    gap="10px"
    sx={{ alignItems: "center", justifyContent: "center", textAlign: "center", padding: "28px 12px", flex: 1 }}
  >
    <Box
      aria-hidden
      sx={{
        width: "44px",
        height: "44px",
        borderRadius: "14px",
        background: DS.accentSoft,
        color: DS.accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="m7 16 4-4 3 3 5-7" />
      </svg>
    </Box>
    <VBox gap="2px">
      <Typo sx={{ fontSize: "14px", fontWeight: 650, color: DS.ink }}>{title}</Typo>
      {hint && <Typo sx={{ fontSize: "13px", color: DS.muted }}>{hint}</Typo>}
    </VBox>
  </VBox>
);

const Notice = ({
  tone,
  children,
  label,
}: {
  tone: "warning" | "error";
  children: string;
  label?: string;
}) => (
  <Box
    role="status"
    aria-label={label}
    sx={{
      padding: "10px 12px",
      borderRadius: "10px",
      color: tone === "warning" ? "#92400E" : "#991B1B",
      backgroundColor: tone === "warning" ? "#FFFBEB" : "#FEF2F2",
      border: `1px solid ${tone === "warning" ? "#FDE68A" : "#FECACA"}`,
    }}
  >
    <Typo sx={{ fontSize: "13px" }}>{children}</Typo>
  </Box>
);

const ListCard = ({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <ChartCard title={title} subtitle={subtitle} action={action}>
    <VBox gap="0px">{children}</VBox>
  </ChartCard>
);

/** One ranked row: position, avatar, name, amount on the right and a thin share bar. */
const RankedRow = ({
  rank,
  name,
  amount,
  amountHint,
  detail,
  percent,
  onClick,
}: {
  rank: number;
  name: string;
  amount: string;
  amountHint?: string;
  detail?: string;
  percent: number;
  onClick?: () => void;
}) => (
  <VBox
    gap="8px"
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    aria-label={onClick ? `Open details: ${name}` : undefined}
    onClick={onClick}
    onKeyDown={
      onClick
        ? (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    sx={{
      padding: "12px 10px",
      margin: "0 -10px",
      borderRadius: "12px",
      cursor: onClick ? "pointer" : "default",
      transition: "background-color .15s ease",
      "&:hover": { backgroundColor: onClick ? DS.soft : "transparent" },
      "&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "-2px" },
    }}
  >
    <HBox gap="12px" sx={{ alignItems: "center" }}>
      <Typo
        sx={{
          width: "20px",
          textAlign: "center",
          fontSize: "13px",
          fontWeight: 650,
          color: rank === 1 ? DS.accent : DS.faint,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {rank}
      </Typo>
      <Avatar name={name} size={36} />
      <VBox gap="1px" sx={{ minWidth: 0, flex: "1 1 0" }}>
        <Typo
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: DS.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typo>
        {detail && (
          <Typo sx={{ fontSize: "12px", color: DS.muted, overflowWrap: "anywhere" }}>
            {detail}
          </Typo>
        )}
      </VBox>
      <VBox gap="1px" sx={{ alignItems: "flex-end", flexShrink: 0 }}>
        <Typo
          sx={{
            fontSize: "14px",
            fontWeight: 650,
            color: DS.ink,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {amount}
        </Typo>
        {amountHint && (
          <Typo sx={{ fontSize: "12px", color: DS.muted }}>{amountHint}</Typo>
        )}
      </VBox>
    </HBox>
    <Box
      sx={{
        marginLeft: "32px",
        height: "4px",
        borderRadius: "2px",
        backgroundColor: DS.soft,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: `${Math.max(0, Math.min(100, percent))}%`,
          height: "100%",
          borderRadius: "2px",
          background: rank === 1 ? DS.gradient : DS.faint,
        }}
      />
    </Box>
  </VBox>
);

export const DashboardPage = () => {
  const router = useRouter();
  const { selectedIssuer, selectedIssuerId, refreshIssuers } = useIssuer();
  const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY;
  // const [userProfile, setUserProfile] = useState<TUser | null>(null);

  const [spendFilter, setSpendFilter] = useState<"month" | "week" | "day">(
    "month",
  );
  const [analyticsPeriodDays, setAnalyticsPeriodDays] =
    useState<AnalyticsPeriodDays>(SPEND_OVERVIEW_MONTH_DAYS);
  const spendOverviewDays =
    spendFilter === "month"
      ? SPEND_OVERVIEW_MONTH_DAYS
      : spendFilter === "week"
        ? SPEND_OVERVIEW_WEEK_DAYS
        : SPEND_OVERVIEW_DAY_DAYS;
  const [paymentsData] = GD.REQ_PAYMENTS.useRequest();
  const [payoutsData] = GD.REQ_PAYOUTS.useRequest();
  const [userProfile] = GD.REQ_PROFILE.useRequest();
  const [cardsData] = GD.REQ_CARDS.useRequest();
  const [accountsData] = GD.REQ_ACCOUNTS.useRequest();
  const [companiesData] = GD.REQ_COMPANIES.useRequest();
  const [teamsData] = GD.REQ_TEAMS_LIST.useRequest();
  const [usersData] = GD.REQ_USERS.useRequest(undefined, {
    options: { include_teams: true },
  });
  const [teamsMonthResponse, , , teamsMonthBusy] =
    GD.REQ_STATS_TEAMS_MONTH.useRequest(undefined, {
      currency: issuerCurrency,
      entity: "card",
      tx_type: "spent",
    });
  const [topTeamsResponse, loadTopTeams, , topTeamsBusy] =
    GD.REQ_STATS_TOP_TEAMS.useRequest(undefined, {
      days: analyticsPeriodDays,
      limit: 4,
      currency: issuerCurrency,
      entity: "card",
      tx_type: "spent",
    });
  const [topBuyersResponse, loadTopBuyers, , topBuyersBusy] =
    GD.REQ_STATS_TOP_BUYERS.useRequest(undefined, {
      days: analyticsPeriodDays,
      limit: 4,
      currency: issuerCurrency,
      entity: "card",
      tx_type: "spent",
    });
  const topStatsRequestRef = useRef({
    days: analyticsPeriodDays,
    currency: issuerCurrency,
  });
  const [recentActivityResponse, , , recentActivityBusy] =
    GD.REQ_COMPANY_TRANSACTIONS_CARDS.useRequest(undefined, {
      page: 0,
      limit: 10,
    });
  // const [cardHoldersData] = GD.REQ_CARDHOLDERS.useRequest();

  const [viewMode, setViewMode] = useState<"teams" | "buyers">("teams");
  const [declinedMode, setDeclinedMode] = useState<DeclinedMode>("teams");
  const [spendOverviewResponse, setSpendOverviewResponse] =
    useState<IAPIResponse<IStatsOverviewStats> | null>(null);
  const [spendTransactionsResponse, setSpendTransactionsResponse] =
    useState<IAPIResponse<ICompanyTransactionsResponse> | null>(null);
  const [spendOverviewBusy, setSpendOverviewBusy] = useState(false);
  const [transactionActivityResponse, setTransactionActivityResponse] =
    useState<IAPIResponse<ICompanyTransactionsResponse> | null>(null);
  const [transactionActivityBusy, setTransactionActivityBusy] = useState(false);

  useEffect(() => {
    void refreshIssuers(true);
  }, [refreshIssuers]);

  useEffect(() => {
    const previous = topStatsRequestRef.current;
    if (
      previous.days === analyticsPeriodDays &&
      previous.currency === issuerCurrency
    ) {
      return;
    }

    topStatsRequestRef.current = {
      days: analyticsPeriodDays,
      currency: issuerCurrency,
    };
    const params = {
      days: analyticsPeriodDays,
      limit: 4,
      currency: issuerCurrency,
      entity: "card" as const,
      tx_type: "spent" as const,
    };

    loadTopTeams(params);
    loadTopBuyers(params);
  }, [analyticsPeriodDays, issuerCurrency, loadTopBuyers, loadTopTeams]);

  const handleCardsStatusClick = (entry?: CardsStatusChartEntry) => {
    if (!entry || entry.value <= 0) return;

    router.navigate("cards", undefined, {
      status: entry.name.toLowerCase() === "active" ? "active" : "inactive",
    });
  };

  useEffect(() => {
    let isActive = true;
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - spendOverviewDays);

    setSpendOverviewBusy(true);
    setSpendOverviewResponse(null);
    setSpendTransactionsResponse(null);

    void Promise.all([
      GD.REQ_STATS_OVERVIEW.request({
        days: spendOverviewDays,
        currency: issuerCurrency,
        entity: "card",
        tx_type: "spent",
      }),
      GD.REQ_COMPANY_TRANSACTIONS_ALL.request({
        from: toDashboardDateKey(startDate),
        to: toDashboardDateKey(endDate),
        currency: issuerCurrency,
        entity: "card",
        tx_type: "spent",
        no_limit: true,
      }),
    ])
      .then(([overviewResponse, transactionsResponse]) => {
        if (!isActive) return;

        setSpendOverviewResponse(overviewResponse);
        setSpendTransactionsResponse(transactionsResponse);
      })
      .catch(() => {
        if (isActive) {
          const error = {
            code: -1,
            message: "Could not load turnover overview",
          };
          setSpendOverviewResponse({ error });
          setSpendTransactionsResponse({ error });
        }
      })
      .finally(() => {
        if (isActive) setSpendOverviewBusy(false);
      });

    return () => {
      isActive = false;
    };
  }, [issuerCurrency, spendOverviewDays]);

  useEffect(() => {
    let isActive = true;
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (analyticsPeriodDays - 1));

    setTransactionActivityBusy(true);
    setTransactionActivityResponse(null);

    void GD.REQ_COMPANY_TRANSACTIONS_ALL.request({
      from: toDashboardDateKey(startDate),
      to: toDashboardDateKey(endDate),
      currency: issuerCurrency,
      entity: "card",
      tx_type: "spent",
      no_limit: true,
    })
      .then((response) => {
        if (isActive) setTransactionActivityResponse(response);
      })
      .catch(() => {
        if (isActive) {
          setTransactionActivityResponse({
            error: { code: -1, message: "Could not load transaction activity" },
          });
        }
      })
      .finally(() => {
        if (isActive) setTransactionActivityBusy(false);
      });

    return () => {
      isActive = false;
    };
  }, [analyticsPeriodDays, issuerCurrency]);

  const stats = useMemo(() => {
    const payments = (paymentsData?.data || []) as IPayment[];
    const payouts = (payoutsData?.data || []) as IPayout[];
    // const cardHolders = (cardHoldersData?.data || []) as ICardHolderSaved[];
    const cards = (cardsData?.data || []) as ICardSaved[];
    const accounts = (accountsData?.data || []) as IAccountSaved[];
    const companies = (companiesData?.data || []) as ICompanyRaw[];
    const teams = (teamsData?.data || []) as ITeamSaved[];
    const users = (usersData?.data || []) as IUserSaved[];
    const spendOverview = spendOverviewResponse?.data;
    const spendTransactions = spendTransactionsResponse?.data;
    const teamsMonth = teamsMonthResponse?.data;
    const topTeamsStats = topTeamsResponse?.data;
    const topBuyersStats = topBuyersResponse?.data;
    const now = new Date();
    const declinedDateKeys = buildDashboardDateKeys(analyticsPeriodDays, now);
    const declinedDateKeySet = new Set(declinedDateKeys);
    const transactionActivity = (
      transactionActivityResponse?.data?.transactions || []
    ).filter((transaction) =>
      declinedDateKeySet.has(toDashboardDateKey(transaction.created_at)),
    );
    const declinedBreakdown = buildDashboardDeclinedBreakdown(
      transactionActivity,
      {
        teams: new Map(teams.map((team) => [team.uid, team.name])),
        buyers: new Map(users.map((user) => [user.uid, user.name])),
      },
    );
    const buyerTransactionCounts =
      countDashboardTransactionsByBuyer(transactionActivity);
    const transactionStatusSummary =
      summarizeDashboardTransactionStatuses(transactionActivity);
    const transactionStatusTrend = buildDashboardStatusTrend(
      transactionActivity,
      declinedDateKeys,
    );

    const totalIncoming = Number(spendOverview?.totals.amount_spent ?? 0);
    const totalOutgoing = payouts.reduce(
      (acc, curr) => acc + (Number(curr.amount) || 0),
      0,
    );
    const successPayments = payments.filter(
      (p) => p.status === "success",
    ).length;
    const successPayouts = payouts.filter((p) => p.status === "success").length;
    const mainBalance = selectedIssuer?.balance ?? 0;
    const companyBuyersBalance = selectedIssuer?.buyers_balance ?? 0;
    const buyersBalance = getDashboardBuyerBalance(
      companyBuyersBalance,
      users,
      userProfile?.company_role,
      teams.map((team) => team.uid),
    );
    const commissionTotal = Number(
      spendOverview?.totals.amount_commission || 0,
    );
    const userBalance =
      userProfile?.team_balances?.find(
        (account) => account.issuer === selectedIssuerId,
      )?.team_balance ??
      userProfile?.team_balance ??
      0;

    const dailyActivity = buildDashboardDailyActivity(
      spendTransactions?.transactions || [],
    );
    const todayKey = toDashboardDateKey(now);
    const dailyIncoming = Number(
      spendOverview?.daily.find((point) => point.date === todayKey)
        ?.amount_spent || 0,
    );

    // Users
    // const activeCardHolders = cardHolders.filter(ch => ch.status === "approved").length;
    const activeCards = cards.filter((c) => c.status === "active").length;
    const cardsBalance = sumDashboardCardBalances(cards);

    const spendOverviewData = (spendOverview?.daily || []).map((point) => {
      const pointDate = new Date(`${point.date}T00:00:00`);
      const activity = dailyActivity.get(point.date);

      return {
        name: pointDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        }),
        fullDate: pointDate.toLocaleDateString("en-GB", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
        spend: Number(point.amount_spent || 0),
        transactions: activity?.transactions || 0,
      };
    });

    let pieChartData: { name: string; value: number; id?: string }[] = [];
    let chartTitle = "Team turnover";

    if (viewMode === "teams") {
      pieChartData = (teamsMonth?.teams || []).map((team) => ({
        name: team.team_name || "Unknown Team",
        value: Number(team.amount || 0),
        id: team.team_uid,
      }));
    } else {
      chartTitle = "Top buyers";
      pieChartData = (topBuyersStats?.buyers || []).map((buyer) => ({
        name: buyer.user_name || "Unknown Buyer",
        value: Number(buyer.amount || 0),
        id: buyer.user_uid,
      }));
    }

    pieChartData.sort((a, b) => b.value - a.value);

    const topTeams = (topTeamsStats?.teams || []).map((entry) => ({
      name: entry.team_name || "Unknown Team",
      spend: Number(entry.amount || 0),
      uid: entry.team_uid,
      team: teams.find((team) => team.uid === entry.team_uid),
    }));

    const buyerCardFinancials = buildBuyerCardFinancials(cards);
    const topBuyers = (topBuyersStats?.buyers || []).map((entry) => ({
      name: entry.user_name || "Unknown Buyer",
      spend: Number(entry.amount || 0),
      uid: entry.user_uid,
      user: users.find((user) => user.uid === entry.user_uid),
      cardFinancials: buyerCardFinancials.get(entry.user_uid),
      transactionCount: buyerTransactionCounts.get(entry.user_uid) || 0,
    }));
    const declinedByTeamChart = buildDashboardDeclinedChart(
      declinedBreakdown.teams,
      declinedDateKeys,
      DASHBOARD_SERIES_COLORS,
    );
    const declinedByBuyerChart = buildDashboardDeclinedChart(
      declinedBreakdown.buyers,
      declinedDateKeys,
      DASHBOARD_SERIES_COLORS,
    );

    return {
      finance: {
        incoming: totalIncoming,
        outgoing: totalOutgoing,
        txCount: payments.length + payouts.length,
        successRate:
          ((successPayments + successPayouts) /
            (payments.length + payouts.length || 1)) *
          100,
        dailyIncoming,
        mainBalance,
        buyersBalance,
        commissionTotal,
        userBalance: userBalance,
        cardsBalance,
        transactionStatus: transactionStatusSummary,
      },
      users: {
        // total: cardHolders.length,
        // active: activeCardHolders,
        cards: cards.length,
        activeCards: activeCards,
      },
      crm: {
        accounts: accounts.length,
        companies: companies.length,
        teams: teams.length,
      },
      charts: {
        volume: [
          { name: "Income", amount: totalIncoming, fill: "#4caf50" },
          { name: "Outcome", amount: totalOutgoing, fill: "#f44336" },
        ],
        status: [
          {
            name: "Success",
            value: successPayments + successPayouts,
            color: "#4caf50",
          },
          {
            name: "Other",
            value:
              payments.length +
              payouts.length -
              (successPayments + successPayouts),
            color: "#ff9800",
          },
        ],
        cards: [
          { name: "Active", value: activeCards, color: "#2196f3" },
          {
            name: "Inactive",
            value: cards.length - activeCards,
            color: "#e0e0e0",
          },
        ],
        weeklySpend: spendOverviewData,
        pieChartData,
        pieChartHasData: pieChartData.some((entry) => entry.value > 0),
        pieChartTitle: chartTitle,
        topTeams,
        topBuyers,
        statusTrend: transactionStatusTrend,
        declined: {
          teams: declinedByTeamChart,
          buyers: declinedByBuyerChart,
        },
      },
    };
  }, [
    paymentsData,
    payoutsData,
    cardsData,
    accountsData,
    companiesData,
    teamsData,
    usersData,
    spendOverviewResponse,
    spendTransactionsResponse,
    teamsMonthResponse,
    topTeamsResponse,
    topBuyersResponse,
    analyticsPeriodDays,
    transactionActivityResponse,
    viewMode,
    userProfile?.team_balance,
    userProfile?.team_balances,
    userProfile?.company_role,
    selectedIssuerId,
    selectedIssuer?.balance,
    selectedIssuer?.buyers_balance,
  ]);

  // Effect to detect Team Lead single team
  useEffect(() => {
    if (userProfile?.company_role === USER_ROLE.TEAM_LEAD && teamsData?.data) {
      const teams = teamsData.data as ITeamSaved[];
      // If team lead sees only 1 team (his own), switch to buyers immediately
      if (teams.length === 1) {
        setViewMode("buyers");
      }
    }
  }, [userProfile, teamsData]);

  const recentTransactions = recentActivityResponse?.data?.transactions || [];
  // Wait for every base list (an error still counts as answered), so no tile shows a zero placeholder as data.
  const isLoading =
    [cardsData, accountsData, companiesData, teamsData, usersData].some(
      (response) => !response,
    ) ||
    teamsMonthBusy ||
    topTeamsBusy ||
    topBuyersBusy ||
    recentActivityBusy;
  const isSpendLoading = spendOverviewBusy;
  const isDeclinedLoading = transactionActivityBusy;
  const isTopBuyersLoading = topBuyersBusy || transactionActivityBusy;
  const transactionActivityError = transactionActivityResponse?.error;
  const transactionActivityIncomplete = isNoLimitTransactionResponseIncomplete(
    transactionActivityResponse?.data,
  );
  const spendTransactionCountsIncomplete =
    isNoLimitTransactionResponseIncomplete(spendTransactionsResponse?.data);
  const declinedChart = stats.charts.declined[declinedMode];
  const declinedCurrency = issuerCurrency;
  const hasDeclinedAmountData = declinedChart.amountsAvailable;
  const hasDeclinedData =
    declinedChart.series.length > 0 && declinedChart.total > 0;
  const openRecentTransaction = (transaction: ICompanyTrxTransaction) => {
    S_UNIVERSAL_DIALOG.invoke({
      label: "Transaction Details",
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
      sx: { maxWidth: "600px", width: "100%" },
      content: <TransactionDetails transaction={transaction} />,
    });
  };

  const money = (amount: number, currency = issuerCurrency) =>
    formatCurrencyWithNumberToSymbolWithNumber(amount, currency);
  const turnoverIsEmpty = stats.charts.weeklySpend.every(
    (d) => d.spend === 0 && d.transactions === 0,
  );
  const spendPeriodOptions = [
    { key: "month" as const, label: "Month" },
    { key: "week" as const, label: "Week" },
    { key: "day" as const, label: "Today" },
  ];
  const analyticsPeriodOptions = ANALYTICS_PERIOD_OPTIONS.map((days) => ({
    key: days,
    label: `${days} days`,
    ariaLabel: `Show analytics for the last ${days} days`,
  }));
  const loadingValue = (width = "140px") => (
    <Skeleton busy width={width} height="36px" />
  );

  const openTopUp = () => {
    S_UNIVERSAL_DIALOG.invoke({
      content: (
        <FormParser
          key="topUp"
          onSubmit={async (data) => {
            const parsedAmount = parsePositiveAmountToMinorUnits(data.amount);
            if (typeof parsedAmount.amount !== "number")
              return amountValidationError(
                parsedAmount.error || "Enter a valid amount.",
              );
            const amount = parsedAmount.amount;
            const res = await GD.REQ_COMPANY_TOPUP.request({
              amount: amount,
              currency: issuerCurrency,
            });
            if (res.error) {
              return res;
            }

            await Promise.all([
              GD.REQ_COMPANIES.request({ force: true }),
              refreshIssuers(true),
            ]);
            toast.success("Company topped up successfully");
            S_UNIVERSAL_DIALOG.invoke(null);
            return res;
          }}
          onRequest={async () => {
            const template = (await GD.REQ_COMPANY_TOPUP_TEMPLATE.request())
              .data!;
            template.currency = issuerCurrency;
            return template;
          }}
          primaryButton={{ title: "Top Up" }}
          secondaryButton={{
            title: "Cancel",
            onClick: () => S_UNIVERSAL_DIALOG.invoke(null),
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  const openTeam = (team: ITeamSaved) => {
    S_UNIVERSAL_DIALOG.invoke({
      sx: {
        padding: "0px",
        width: "min(900px, calc(100vw - 32px))",
        maxWidth: "900px",
      },
      content: (
        <TeamDetails
          team={team}
          onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
          onUpdate={async () => {
            await GD.REQ_TEAMS_LIST.request();
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  const openBuyer = (uid: string) => {
    S_UNIVERSAL_DIALOG.invoke({
      sx: { maxWidth: MAX_USER_DETAILS_WIDTH },
      contentPadding: "0px",
      content: (
        <UserDetails
          userUid={uid}
          onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
          onUpdate={async () => {
            await GD.REQ_USERS.request();
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  const pieTotal = stats.charts.pieChartData.reduce(
    (sum, entry) => sum + entry.value,
    0,
  );

  const renderTurnoverChart = () => (
    <ChartCard
      title="Turnover overview"
      subtitle={
        spendOverviewDays === 1
          ? "Card turnover and transaction count today"
          : `Card turnover and transaction count for the last ${spendOverviewDays} days`
      }
    >
      {spendTransactionCountsIncomplete && !isSpendLoading && (
        <Notice tone="warning" label="Partial turnover transaction counts">
          Partial transaction history: turnover amounts are complete, but transaction counts include loaded rows only.
        </Notice>
      )}
      {isSpendLoading ? (
        <Skeleton busy width="100%" height="240px" />
      ) : turnoverIsEmpty ? (
        <EmptyNote
          title="No data for this period"
          hint="Card turnover appears here once cards are used."
        />
      ) : (
        <Box sx={{ width: "100%", height: "240px" }}>
          <PositiveSizeChart>
            <LineChart
              data={stats.charts.weeklySpend}
              margin={{ top: 5, right: 10, bottom: 5, left: 10 }}
            >
              <Legend
                verticalAlign="top"
                align="right"
                height={28}
                iconType="circle"
                wrapperStyle={{ fontSize: "12px", color: DS.muted }}
              />
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={DS.line} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={AXIS_TICK} dy={10} minTickGap={24} />
              <YAxis
                yAxisId="spend"
                axisLine={false}
                tickLine={false}
                tick={AXIS_TICK}
                width={50}
                dataKey="spend"
                tickFormatter={(value) => money(Number(value) || 0)}
              />
              <YAxis
                yAxisId="activity"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ ...AXIS_TICK, fill: "#3F6E9A" }}
                width={28}
                allowDecimals={false}
              />
              <Tooltip
                {...TOOLTIP_PROPS}
                formatter={(value: number | string | undefined, name) =>
                  name === "Transactions"
                    ? [Number(value) || 0, "Transactions"]
                    : [money(Number(value) || 0), "Turnover"]
                }
                labelFormatter={(_label, payload) =>
                  `Date: ${payload?.[0]?.payload?.fullDate || _label}`
                }
              />
              <Line
                yAxisId="spend"
                type="monotone"
                dataKey="spend"
                name="Turnover"
                stroke={DASHBOARD_BRAND}
                strokeWidth={2.5}
                dot={{ r: 3, fill: DASHBOARD_BRAND, strokeWidth: 1.5, stroke: "#fff" }}
                activeDot={{ r: 5, strokeWidth: 0, fill: DASHBOARD_BRAND }}
              />
              <Line
                yAxisId="activity"
                type="monotone"
                dataKey="transactions"
                name="Transactions"
                stroke="#3F6E9A"
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={{ r: 2.5, fill: "#3F6E9A", strokeWidth: 0 }}
                activeDot={{ r: 4, strokeWidth: 0, fill: "#3F6E9A" }}
              />
            </LineChart>
          </PositiveSizeChart>
        </Box>
      )}
    </ChartCard>
  );

  const renderTurnoverSplit = () => (
    <ChartCard
      title={stats.charts.pieChartTitle}
      subtitle={
        viewMode === "teams"
          ? "How turnover is split. Select a team to drill into its buyers."
          : "How turnover is split between buyers"
      }
      action={
        viewMode === "buyers" ? (
          <TextLink onClick={() => setViewMode("teams")}>Back</TextLink>
        ) : undefined
      }
    >
      {isLoading ? (
        <Skeleton busy width="100%" height="240px" />
      ) : !stats.charts.pieChartHasData ? (
        <EmptyNote
          title="No team turnover yet"
          hint="Spending by team appears here once cards are used."
        />
      ) : (
        <>
          <Box sx={{ width: "100%", height: "180px" }}>
            <PositiveSizeChart>
              <PieChart>
                <Pie
                  data={stats.charts.pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="58%"
                  outerRadius="85%"
                  paddingAngle={3}
                  dataKey="value"
                  onClick={(data) => {
                    if (viewMode === "teams" && data.id) setViewMode("buyers");
                  }}
                  style={{
                    cursor: viewMode === "teams" ? "pointer" : "default",
                    outline: "none",
                  }}
                >
                  {stats.charts.pieChartData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                      stroke="none"
                    />
                  ))}
                </Pie>
                <Tooltip
                  {...TOOLTIP_PROPS}
                  formatter={(value: number | string | undefined) => [
                    money(Number(value) || 0),
                    "Amount",
                  ]}
                />
              </PieChart>
            </PositiveSizeChart>
          </Box>
          <VBox gap="6px">
            {stats.charts.pieChartData.slice(0, 5).map((entry, index) => (
              <HBox key={entry.id ?? entry.name} gap="8px" sx={{ alignItems: "center" }}>
                <Box
                  aria-hidden
                  sx={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "50%",
                    flexShrink: 0,
                    backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                  }}
                />
                <Typo
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    fontSize: "13px",
                    color: DS.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {entry.name}
                </Typo>
                <Typo
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: DS.ink,
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {money(entry.value)}
                </Typo>
                <Typo
                  sx={{
                    width: "42px",
                    textAlign: "right",
                    fontSize: "12px",
                    color: DS.muted,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {pieTotal > 0 ? `${Math.round((entry.value / pieTotal) * 100)}%` : "—"}
                </Typo>
              </HBox>
            ))}
          </VBox>
        </>
      )}
    </ChartCard>
  );

  const renderCardSpendStatus = () => (
    <ChartCard
      title="Card-spend status"
      subtitle={`Daily card transaction results for the last ${analyticsPeriodDays} days`}
      action={
        <PeriodToggle
          label="Analytics period"
          options={analyticsPeriodOptions}
          value={analyticsPeriodDays}
          onChange={setAnalyticsPeriodDays}
        />
      }
    >
      {transactionActivityIncomplete &&
        !transactionActivityBusy &&
        !transactionActivityError && (
          <Notice tone="warning" label="Partial card-spend statistics">
            Partial data: the backend returned an incomplete transaction selection. Counts, rates, and declined amounts below cover loaded rows only and are not exact.
          </Notice>
        )}
      {transactionActivityBusy ? (
        <Skeleton busy width="100%" height="72px" />
      ) : transactionActivityError ? (
        <Notice tone="error">Transaction activity summary is unavailable.</Notice>
      ) : stats.finance.transactionStatus.total > 0 ? (
        <CardSpendStatusRatio
          successful={stats.finance.transactionStatus.successful}
          declined={stats.finance.transactionStatus.declined}
          successRate={stats.finance.transactionStatus.successRate}
          declineRate={stats.finance.transactionStatus.declineRate}
          pending={stats.finance.transactionStatus.pending}
          incomplete={transactionActivityIncomplete}
          onSelect={(bucket) =>
            router.navigate("transactions", undefined, {
              // The transactions filter has no "declined": declined/failed card spend is stored as "error".
              status: bucket === "successful" ? "completed" : bucket === "declined" ? "error" : "pending",
              type: "spent",
              days: String(analyticsPeriodDays),
            })
          }
        />
      ) : (
        <EmptyNote
          title="No card-spend activity in this period"
          hint="Results appear here once cards are used."
        />
      )}
      {isDeclinedLoading ? (
        <Skeleton busy width="100%" height="200px" />
      ) : stats.finance.transactionStatus.successful +
          stats.finance.transactionStatus.declined ===
        0 ? null : (
        <>
          <Box sx={{ width: "100%", height: "220px" }}>
            <PositiveSizeChart>
              <LineChart
                data={stats.charts.statusTrend}
                margin={{ top: 5, right: 18, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={DS.line} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={AXIS_TICK} dy={10} minTickGap={18} />
                <YAxis axisLine={false} tickLine={false} tick={AXIS_TICK} width={32} allowDecimals={false} />
                <Legend
                  verticalAlign="top"
                  align="left"
                  height={28}
                  iconType="circle"
                  wrapperStyle={{ color: DS.muted, fontSize: "12px", fontWeight: 600 }}
                />
                <Tooltip
                  content={(props) => (
                    <CardSpendStatusTooltip
                      {...props}
                      incomplete={transactionActivityIncomplete}
                    />
                  )}
                />
                {stats.finance.transactionStatus.successful > 0 && (
                  <Line
                    type="monotone"
                    dataKey="successful"
                    name="Successful"
                    stroke="#10B981"
                    strokeWidth={2.5}
                    dot={{ r: 2.5, fill: "#10B981", strokeWidth: 0 }}
                    activeDot={{ r: 4, strokeWidth: 0, fill: "#059669" }}
                  />
                )}
                {stats.finance.transactionStatus.declined > 0 && (
                  <Line
                    type="monotone"
                    dataKey="declined"
                    name="Declined"
                    stroke="#EF4444"
                    strokeWidth={2.5}
                    dot={{ r: 2.5, fill: "#EF4444", strokeWidth: 0 }}
                    activeDot={{ r: 4, strokeWidth: 0, fill: "#DC2626" }}
                  />
                )}
                {stats.finance.transactionStatus.total === 0 && (
                  <Line
                    type="monotone"
                    dataKey="successful"
                    name="Transactions"
                    stroke="#CBD5E1"
                    strokeWidth={2}
                    dot={{ r: 2.5, fill: "#CBD5E1", strokeWidth: 0 }}
                    activeDot={{ r: 4, strokeWidth: 0, fill: "#94A3B8" }}
                  />
                )}
              </LineChart>
            </PositiveSizeChart>
          </Box>
          {hasDeclinedData && (
            <VBox
              gap="10px"
              sx={{ paddingTop: "14px", borderTop: `1px solid ${DS.line}` }}
            >
              <HBox
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 650 }}>
                  Declined breakdown
                </Typo>
                <PeriodToggle
                  label="Declined breakdown by"
                  options={[
                    { key: "teams" as DeclinedMode, label: "Teams" },
                    { key: "buyers" as DeclinedMode, label: "Buyers" },
                  ]}
                  value={declinedMode}
                  onChange={setDeclinedMode}
                />
              </HBox>
              <HBox sx={{ gap: "8px", flexWrap: "wrap" }}>
                {declinedChart.series.map((series) => (
                  <HBox
                    key={series.key}
                    sx={{
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 12px",
                      borderRadius: "999px",
                      border: `1px solid ${DS.line}`,
                      backgroundColor: DS.soft,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: series.color,
                      }}
                    />
                    <Typo sx={{ color: DS.ink, fontSize: "13px", fontWeight: 600 }}>
                      {series.name}
                    </Typo>
                    <Typo
                      sx={{
                        color: DS.muted,
                        fontSize: "13px",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {series.total}
                      {hasDeclinedAmountData
                        ? ` · ${money(series.totalAmount, declinedCurrency)}`
                        : ""}
                    </Typo>
                  </HBox>
                ))}
              </HBox>
            </VBox>
          )}
        </>
      )}
    </ChartCard>
  );

  const renderTopTeams = () => {
    const maxSpend = Math.max(1, ...stats.charts.topTeams.map((s) => s.spend));
    return (
      <ListCard
        title="Top teams"
        subtitle={`Card spending over the last ${analyticsPeriodDays} days`}
      >
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton busy key={i} width="100%" height="48px" sx={{ marginBottom: "8px" }} />
          ))
        ) : stats.charts.topTeams.length === 0 ? (
          <EmptyNote title="No team turnover for this period" />
        ) : (
          stats.charts.topTeams.map((entry, idx) => (
            <RankedRow
              key={entry.uid ?? idx}
              rank={idx + 1}
              name={entry.name}
              amount={money(entry.spend)}
              percent={(entry.spend / maxSpend) * 100}
              onClick={entry.team ? () => openTeam(entry.team!) : undefined}
            />
          ))
        )}
      </ListCard>
    );
  };

  const renderTopBuyers = () => {
    const maxSpend = Math.max(1, ...stats.charts.topBuyers.map((s) => s.spend));
    return (
      <ListCard
        title="Top buyers"
        subtitle={`Card spending over the last ${analyticsPeriodDays} days`}
      >
        {transactionActivityIncomplete && !isTopBuyersLoading && (
          <Notice tone="warning">
            Transaction counts are partial; spend amounts remain complete.
          </Notice>
        )}
        {isTopBuyersLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton busy key={i} width="100%" height="48px" sx={{ marginBottom: "8px" }} />
          ))
        ) : topBuyersResponse?.error ? (
          <Notice tone="error">Could not load top buyers.</Notice>
        ) : stats.charts.topBuyers.length === 0 ? (
          <EmptyNote title="No buyer spending for this period" />
        ) : (
          stats.charts.topBuyers.map((entry, idx) => {
            const balance =
              entry.user?.team_balance === undefined
                ? "—"
                : money(entry.user.team_balance);
            const cardCount = entry.cardFinancials?.cardsCount ?? 0;
            return (
              <RankedRow
                key={entry.uid ?? idx}
                rank={idx + 1}
                name={entry.name}
                amount={money(entry.spend)}
                amountHint={
                  transactionActivityError
                    ? "Transaction count unavailable"
                    : `${transactionActivityIncomplete ? "At least " : ""}${entry.transactionCount} ${entry.transactionCount === 1 ? "transaction" : "transactions"}`
                }
                detail={`Available ${balance} · ${cardCount} ${cardCount === 1 ? "card" : "cards"} · ${money(entry.cardFinancials?.cardsBalance ?? 0)} on cards`}
                percent={(entry.spend / maxSpend) * 100}
                onClick={entry.user ? () => openBuyer(entry.uid) : undefined}
              />
            );
          })
        )}
      </ListCard>
    );
  };

  const renderRecentActivity = () => (
    <ChartCard
      title="Recent activity"
      subtitle="Latest card transactions"
      action={
        <TextLink onClick={() => router.navigate("transactions")}>
          View all
        </TextLink>
      }
    >
      <VBox gap="0px">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <Skeleton busy key={i} width="100%" height="48px" sx={{ marginBottom: "8px" }} />
            ))
          : recentTransactions.slice(0, 5).map((transaction, i) => {
              const income = transaction.direction === "income";
              const amount = getTransactionAmountPresentation(transaction);
              const label =
                getTransactionDisplayLabel(transaction) || "Transaction";
              return (
                <HBox
                  key={transaction.id ?? i}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open transaction details: ${getTransactionDisplayLabel(transaction) || transaction.id}`}
                  onClick={() => openRecentTransaction(transaction)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openRecentTransaction(transaction);
                    }
                  }}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "12px 10px",
                    margin: "0 -10px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "background-color .15s ease",
                    "&:hover": { backgroundColor: DS.soft },
                    "&:focus-visible": {
                      outline: `2px solid ${DS.accent}`,
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  <HBox gap="12px" sx={{ alignItems: "center", minWidth: 0 }}>
                    <Box
                      aria-hidden
                      sx={{
                        width: "38px",
                        height: "38px",
                        minWidth: "38px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: income ? "#ECFDF5" : "#FEF2F2",
                        color: income ? "#047857" : "#B42318",
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d={income ? "M17 7 7 17M7 17h8M7 17V9" : "M7 17 17 7M17 7H9M17 7v8"} />
                      </svg>
                    </Box>
                    <VBox gap="2px" sx={{ minWidth: 0 }}>
                      <Typo
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: DS.ink,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Typo>
                      <Typo sx={{ fontSize: "12px", color: DS.muted }}>
                        {formatActivityDate(transaction.created_at)} · ID{" "}
                        {String(transaction.id).substring(0, 6)}…
                      </Typo>
                    </VBox>
                  </HBox>
                  <VBox gap="4px" sx={{ alignItems: "flex-end", flexShrink: 0 }}>
                    <Typo
                      sx={{
                        fontSize: "14px",
                        fontWeight: 650,
                        color: amount.color,
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`${amount.sign}${money(amount.amount, transaction.currency || issuerCurrency)}`}
                    </Typo>
                    <StatusBadge status={transaction.status} />
                  </VBox>
                </HBox>
              );
            })}
        {!recentTransactions.length && !isLoading && (
          <EmptyNote title="No recent activity" />
        )}
      </VBox>
    </ChartCard>
  );

  const renderAdminDashboard = () => (
    <VBox gap="16px">
      <HeroStat
        label="Main account balance"
        value={isLoading ? loadingValue("160px") : money(stats.finance.mainBalance)}
        hint={
          <>
            Buyers balance{" "}
            <strong>
              {isLoading ? "—" : money(stats.finance.buyersBalance)}
            </strong>
          </>
        }
        actions={
          <>
            <HeroButton
              ghost
              onClick={() => router.navigate("users", undefined, { role: "buyer" })}
            >
              Buyers list
            </HeroButton>
            {userProfile?.company_role === USER_ROLE.ADMIN &&
              selectedIssuer?.is_default === true && (
                <HeroButton onClick={openTopUp}>Top up account</HeroButton>
              )}
          </>
        }
      />

      <StatGroup
        items={[
          {
            label: "Total card balance",
            value: isLoading ? loadingValue("100px") : money(stats.finance.cardsBalance),
            hint: `Across ${stats.users.cards} loaded card${stats.users.cards === 1 ? "" : "s"}`,
          },
          {
            label: getDashboardPeriodMetricLabel(spendFilter),
            value: isSpendLoading ? loadingValue("100px") : `${stats.finance.incoming > 0 ? "−" : ""}${money(stats.finance.incoming)}`,
            hint: (
              <VBox gap="8px">
                <span>
                  Spent from cards in this period
                  {!isSpendLoading && stats.finance.commissionTotal > 0
                    ? ` · ${money(stats.finance.commissionTotal)} commissions`
                    : ""}
                </span>
                <PeriodToggle
                  label="Spending period"
                  options={spendPeriodOptions}
                  value={spendFilter}
                  onChange={setSpendFilter}
                />
              </VBox>
            ),
          },
          {
            label: "Active teams",
            value: isLoading ? loadingValue("60px") : stats.crm.teams,
            hint: (
              <VBox gap="6px">
                <span>Teams that can use this company account</span>
                <TextLink onClick={() => router.navigate("teams")}>View all teams</TextLink>
              </VBox>
            ),
          },
          ...(userProfile?.company_role !== USER_ROLE.TEAM_LEAD
            ? [
                {
                  label: "Active cards",
                  value: isLoading ? loadingValue("60px") : stats.users.activeCards,
                  hint: (
                    <VBox gap="6px">
                      <span>
                        {stats.users.cards - stats.users.activeCards} inactive of {stats.users.cards} total
                      </span>
                      <TextLink onClick={() => router.navigate("cards")}>Manage cards</TextLink>
                    </VBox>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(300px, 1fr)",
          gap: "16px",
          "@media (max-width: 1040px)": { gridTemplateColumns: "minmax(0, 1fr)" },
        }}
      >
        {renderTurnoverChart()}
        {renderTurnoverSplit()}
      </Box>

      {renderCardSpendStatus()}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: "16px",
          "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
        }}
      >
        {renderTopTeams()}
        {renderTopBuyers()}
      </Box>

      {renderRecentActivity()}
    </VBox>
  );
  const renderTeamLeadDashboard = () => renderAdminDashboard();

  const renderBuyerDashboard = () => (
    <VBox gap="16px">
      <HeroStat
        label="My balance"
        value={isLoading ? loadingValue("140px") : money(stats.finance.userBalance)}
        hint="Funds you can load onto your cards"
      />
      <StatGroup
        items={[
          {
            label: "Spent today",
            value: isLoading ? loadingValue("100px") : money(stats.finance.dailyIncoming),
          },
          {
            label: "Active cards",
            value: isLoading ? loadingValue("60px") : stats.users.activeCards,
          },
        ]}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(300px, 1fr)",
          gap: "16px",
          "@media (max-width: 1040px)": { gridTemplateColumns: "minmax(0, 1fr)" },
        }}
      >
        <ChartCard
          title="My recent turnover"
          subtitle={`Your daily card turnover for the last ${spendOverviewDays} days`}
        >
          {isSpendLoading ? (
            <Skeleton busy width="100%" height="240px" />
          ) : stats.charts.weeklySpend.every((day) => day.spend === 0) ? (
            <EmptyNote title="No card turnover in this period" />
          ) : (
            <Box sx={{ width: "100%", height: "240px" }}>
              <PositiveSizeChart>
                <BarChart
                  data={stats.charts.weeklySpend}
                  margin={{ top: 5, right: 20, bottom: 5, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={DS.line} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={AXIS_TICK} dy={10} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={AXIS_TICK}
                    width={50}
                    tickFormatter={(value) => money(Number(value) || 0)}
                  />
                  <Tooltip
                    {...TOOLTIP_PROPS}
                    cursor={{ fill: "rgba(30, 58, 95, 0.04)" }}
                    formatter={(value: number | string | undefined) => [
                      money(Number(value) || 0),
                      "Turnover",
                    ]}
                  />
                  <Bar dataKey="spend" fill={DASHBOARD_BRAND} radius={[6, 6, 0, 0]} />
                </BarChart>
              </PositiveSizeChart>
            </Box>
          )}
        </ChartCard>

        <ChartCard title="Cards status" subtitle="Active and inactive cards">
          {isLoading ? (
            <Skeleton busy width="100%" height="240px" />
          ) : (
            <Box sx={{ width: "100%", height: "240px" }}>
              <PositiveSizeChart>
                <PieChart>
                  <Pie
                    data={stats.charts.cards}
                    cx="50%"
                    cy="50%"
                    innerRadius="58%"
                    outerRadius="82%"
                    paddingAngle={3}
                    dataKey="value"
                    onClick={handleCardsStatusClick}
                  >
                    {stats.charts.cards.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        stroke="none"
                        style={{
                          cursor: entry.value > 0 ? "pointer" : "default",
                          outline: "none",
                        }}
                      />
                    ))}
                  </Pie>
                  <Tooltip {...TOOLTIP_PROPS} />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" />
                </PieChart>
              </PositiveSizeChart>
            </Box>
          )}
        </ChartCard>
      </Box>
    </VBox>
  );

  const role = userProfile?.company_role;

  return (
    <Page
      sx={{ boxShadow: "none", paddingTop: "24px" }}
      label="Dashboard"
      description="See balances, card activity and spending."
    >
      <VBox>
        {!userProfile ? (
          <VBox gap="16px">
            <Skeleton busy height="120px" />
            <Skeleton busy height="96px" />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr",
                gap: "16px",
                height: "300px",
                "@media (max-width: 768px)": {
                  gridTemplateColumns: "minmax(0, 1fr)",
                  height: "auto",
                },
              }}
            >
              <Skeleton busy height="100%" />
              <Skeleton busy height="100%" />
            </Box>
          </VBox>
        ) : (
          <>
            {role === USER_ROLE.ADMIN && renderAdminDashboard()}
            {role === USER_ROLE.TEAM_LEAD && renderTeamLeadDashboard()}
            {role === USER_ROLE.BUYER && renderBuyerDashboard()}
          </>
        )}
        <Spacer size="40px" />
      </VBox>
    </Page>
  );
};
